import type { BlockSpacing, ResponsiveSpacing, SpacingValues } from '~/types/strapi';

/**
 * Generate CSS properties for spacing values
 */
export function generateSpacingCSS(values: SpacingValues, type: 'margin' | 'padding'): string | null {
  const properties: string[] = [];
  
  if (values.top !== undefined) {
    properties.push(`${type}-top: ${values.top}px`);
  }
  if (values.right !== undefined) {
    properties.push(`${type}-right: ${values.right}px`);
  }
  if (values.bottom !== undefined) {
    properties.push(`${type}-bottom: ${values.bottom}px`);
  }
  if (values.left !== undefined) {
    properties.push(`${type}-left: ${values.left}px`);
  }
  
  return properties.length > 0 ? properties.join('; ') : null;
}

/**
 * Generate custom CSS for responsive spacing values
 */
export function generateCustomSpacingCSS(spacing: ResponsiveSpacing, type: 'margin' | 'padding', className: string): string | null {
  const rules: string[] = [];
  
  // Mobile (default)
  if (spacing.mobile) {
    const mobileCSS = generateSpacingCSS(spacing.mobile, type);
    if (mobileCSS) {
      rules.push(`.${className}--${type}-custom { ${mobileCSS} }`);
    }
  }
  
  // Tablet
  if (spacing.tablet) {
    const tabletCSS = generateSpacingCSS(spacing.tablet, type);
    if (tabletCSS) {
      rules.push(`@media (min-width: 768px) { .${className}--${type}-custom { ${tabletCSS} } }`);
    }
  }
  
  // Desktop
  if (spacing.desktop) {
    const desktopCSS = generateSpacingCSS(spacing.desktop, type);
    if (desktopCSS) {
      rules.push(`@media (min-width: 1024px) { .${className}--${type}-custom { ${desktopCSS} } }`);
    }
  }
  
  // Wide
  if (spacing.wide) {
    const wideCSS = generateSpacingCSS(spacing.wide, type);
    if (wideCSS) {
      rules.push(`@media (min-width: 1280px) { .${className}--${type}-custom { ${wideCSS} } }`);
    }
  }
  
  // XLarge
  if (spacing.xlarge) {
    const xlargeCSS = generateSpacingCSS(spacing.xlarge, type);
    if (xlargeCSS) {
      rules.push(`@media (min-width: 1536px) { .${className}--${type}-custom { ${xlargeCSS} } }`);
    }
  }
  
  return rules.length > 0 ? rules.join('\n') : null;
}

/**
 * Generate CSS classes for margins or paddings based on spacing configuration
 */
export function generateSpacingClasses(type: 'margin' | 'padding', spacing?: BlockSpacing, className?: string): string[] {
  if (!spacing) return [];
  
  const classes: string[] = [];
  
  // If custom spacing is provided, use it instead of spacingType
  if (spacing.customSpacing) {
    const customCSS = generateCustomSpacingCSS(spacing.customSpacing, type, className || 'content-section');
    if (customCSS) {
      // Note: CSS injection should be handled by the calling component
      return [`${className || 'content-section'}--${type}-custom`];
    }
  }
  
  // Use predefined spacing type if no custom spacing
  if (spacing.spacingType && spacing.spacingType !== 'none') {
    classes.push(`content-section--${type}-${spacing.spacingType}`);
  }
  
  return classes;
} 